import UserService from "./UserService";
import UtilityService from "./UtilityService";
import Kichiri from "./KichiriService";

import moment from "moment";
import ConfigurationService from "./ConfigurationService";
import ClientService from "./ClientService";

const BookkeepingService = {
	/**
	 * Downloads bookkeeping summaries as an Excel file for a specific client.
	 *
	 * @async
	 * @function downloadBookkeepingSummaries
	 * @param {Object} params - Parameters for downloading bookkeeping summaries.
	 * @param {string} params.clientId - The ID of the client for whom the summaries are generated.
	 * @param {Array<Object>} params.transactions - The list of transactions to be included in the summary.
	 * @param {Object} params.priorYearTrialBalance - The trial balance data for the prior year.
	 * @param {Array<string>} params.classifications - The classifications to be applied to the transactions.
	 * @returns {Promise<void>} A promise that resolves when the file is successfully downloaded.
	 * @throws {Error} Throws an error if the fetch request or file download fails.
	 */
	async downloadBookkeepingSummaries({ clientId, transactions, priorYearTrialBalance, classifications, hasTax }) {
		let user = UserService.getUserData();

		try {
			let { client } = await ClientService.fetchClient({ clientId });

			let response = await fetch(`${ConfigurationService.resolveApi().rest}/tools/bookkeeping/download_summaries`, {
				method: "POST",
				headers: {
					authorization: user.authToken,
					"Content-Type": "application/json"
				},
				body: JSON.stringify({
					clientId,
					transactions,
					priorYearTrialBalance,
					classifications,
					hasTax
				})
			});

			let blob = await response.blob();

			UtilityService.downloadFile({ blob, fileName: `${client.name} Summary.xlsx` });
		} catch (error) {}
	},

	/**
	 * Categorizes a financial transaction by sending data to the bookkeeping classification API.
	 *
	 * @async
	 * @function categorizeTransaction
	 * @param {Object} params - Parameters for the transaction classification.
	 * @param {string} params.clientId - The ID of the client associated with the transaction.
	 * @param {string} params.transactionDescription - A description of the transaction.
	 * @param {number} params.amount - The amount of the transaction.
	 * @param {Array<string>} params.classifications - The classifications for the transaction.
	 * @param {string} params.industry - The industry related to the transaction.
	 * @param {string} params.province - The province where the transaction occurred.
	 * @returns {Promise<Object>} A promise that resolves to the API response as a JSON object.
	 * @throws {Error} Throws an error if the fetch request fails.
	 */
    async categorizeTransaction({ clientId, transactionDescription, amount, classifications, classificationReferences, industry, province, generalLedgerReferences }) {
        let userData = UserService.getUserData();

        try {
            const response = await fetch(`${ConfigurationService.resolveApi().rest}/tools/bookkeeping/classify`, {
                method: "POST",
                headers: {
                    authorization: userData.authToken,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    clientId,
                    transactionDescription,
                    amount,
                    classifications,
                    classificationReferences,
                    industry,
                    province,
                    generalLedgerReferences
                })
            });

            const json = await response.json();
            const result = (json && json.data) ? json.data : json;

            return result;
        } catch (error) {
            return { classification: "Unclassified", tax: "" };
        }
    },

	/**
	 * Retrieves tax rates based on the provided parameters.
	 *
	 * @async
	 * @function getTaxRates
	 * @param {Object} params - The parameters for retrieving tax rates.
	 * @param {string} params.clientId - The ID of the client.
	 * @param {string} params.classification - The classification of the accounts.
	 * @param {string} params.province - The province for which tax rates are needed.
	 * @param {string} params.industry - The industry for which tax rates are needed.
	 * @returns {Promise<Object>} A promise that resolves to the tax rates data.
	 * @throws {Error} Throws an error if the request fails.
	 */
	async getTaxRates({ clientId, account, province, industry }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.classifyAccountsTax(
				{},
				{
					clientId,
					account,
					province,
					industry
				},
				userData.authToken
			);

			response = response.data;

			return await response;
		} catch (error) {}
	},

	/**
	 * Uploads Caseware reference files for a specific client.
	 *
	 * @param {Object} params - The parameters for the upload.
	 * @param {string} params.clientId - The ID of the client.
	 * @param {File[]} params.files - The files to be uploaded.
	 * @param {Array<Object>} params.transactions - The transactions to be included in the upload.
	 * @returns {Promise<Object>} The response from the server.
	 * @throws Will throw an error if the upload fails.
	 */
	async uploadCasewareReference({ clientId, files, transactions }) {
		let userData = UserService.getUserData();

		try {
			let { client } = await ClientService.fetchClient({ clientId });

			let formData = new FormData();

			for (let file of files) {
				formData.append("files", file);
			}
			
			if (transactions && transactions.length > 0) {
				formData.append("transactions", JSON.stringify(transactions));
			}

			let response = await fetch(`${ConfigurationService.resolveApi().rest}/tools/bookkeeping/upload_caseware_reference`, {
				method: "POST",
				body: formData,
				headers: {
					authorization: userData.authToken,
					Accept: "application/json"
				}
			});

			return await response.json();
		} catch (error) {}
	},

	/**
	 * Calculates the tax amount based on the given amount and tax rate.
	 *
	 * @param {Object} params - The parameters for the calculation.
	 * @param {number} params.amount - The amount on which tax is to be calculated.
	 * @param {string} params.tax - The tax rate as a string (e.g., "5%" or "10.5%").
	 * @returns {string} The calculated tax amount as a string with two decimal places.
	 */
	calculateTaxAmount({ amount, tax, hasTax }) {
		if (!hasTax) return 0.0;
		if (!tax && tax !== 0) return 0.0;

		const taxStr = String(tax);
		const match = taxStr.match(/[\d.]+/);
		if (!match) return 0.0;

		const taxRate = parseFloat(match[0]);
		if (!isFinite(taxRate) || isNaN(taxRate)) return 0.0;

		const amt = parseFloat(amount);
		if (!isFinite(amt) || isNaN(amt)) return 0.0;

		const taxAmount = (amt * taxRate) / (100 + taxRate);
		return parseFloat(taxAmount.toFixed(2));
	},

	/**
	 * Extracts transactions from a CSV file.
	 *
	 * @param {Object} params - The parameters object.
	 * @param {File} params.file - The CSV file to extract transactions from.
	 * @returns {Promise<Object>} A promise that resolves to an object containing the extracted transactions.
	 */
	async extractTransactionsFromCsv({ file }) {
		let { name } = file;
		name = name.replace(".csv", "");
		let fileContent = await file.text();
		let lines = fileContent.split("\n");
		let cleanedLines = this.cleanFileArray({ lines });

		const extractedData = cleanedLines.map(item => this.parseTransaction(item));

		let invalidCount = 0;

		let transactions = extractedData.map(transaction => {
			let date = transaction.date;
			let description = transaction.description;
			let merchant = transaction.merchant;
			let amount = -(transaction.amount);
			let bankAccountName = name;

			const hasInvalidDate = !date || !moment(date, "YYYY-MM-DD", true).isValid();
			const hasInvalidAmount = isNaN(amount);

			if (hasInvalidDate || hasInvalidAmount) {
				invalidCount++;
			}

			return {
				date,
				description,
				merchant,
				amount,
				bankAccountName
			};
		});

		return {
			transactions,
			invalidCount
		};
	},

	/**
	 * Normalizes a date string from various formats into a consistent YYYY-MM-DD format.
	 *
	 * @param {string} dateStr - The raw date string from the CSV.
	 * @returns {string|null} The normalized date in YYYY-MM-DD format, or null if invalid.
	 */
	normalizeDate(dateStr) {
		if (!dateStr) return null;

		const trimmed = dateStr.trim();
		if (!trimmed) return null;

		const formats = [
			// ISO 8601 (international standard)
			"YYYY-MM-DD",
			"YYYY/MM/DD",
			"YYYYMMDD",

			// Day-Month-Year (UK, Europe, Australia, India, most of world)
			"DD-MM-YYYY",
			"D-M-YYYY",
			"DD/MM/YYYY",
			"D/M/YYYY",
			"DD.MM.YYYY",
			"D.M.YYYY",
			"DD MM YYYY",

			// Month-Day-Year (US, Canada)
			"MM-DD-YYYY",
			"M-D-YYYY",
			"MM/DD/YYYY",
			"M/D/YYYY",
			"MM.DD.YYYY",
			"M.D.YYYY",

			// Two-digit year variants
			"DD-MM-YY",
			"D-M-YY",
			"DD/MM/YY",
			"D/M/YY",
			"DD.MM.YY",
			"MM-DD-YY",
			"M-D-YY",
			"MM/DD/YY",
			"M/D/YY",
			"MM.DD.YY",
			"YY-MM-DD",
			"YY/MM/DD",

			// Named months - abbreviated (e.g., "15-Jan-2025", "Jan 15, 2025")
			"DD-MMM-YYYY",
			"D-MMM-YYYY",
			"DD MMM YYYY",
			"D MMM YYYY",
			"MMM DD, YYYY",
			"MMM DD YYYY",
			"MMM D, YYYY",
			"MMM D YYYY",
			"DD-MMM-YY",
			"D-MMM-YY",
			"DD MMM YY",
			"MMM DD, YY",
			"MMM DD YY",

			// Named months - full (e.g., "15 January 2025", "January 15, 2025")
			"DD MMMM YYYY",
			"D MMMM YYYY",
			"MMMM DD, YYYY",
			"MMMM DD YYYY",
			"MMMM D, YYYY",
			"MMMM D YYYY",
			"DD MMMM YY",
			"MMMM DD, YY",
			"MMMM DD YY",
		];

		const parsed = moment(trimmed, formats, true);
		if (parsed.isValid()) {
			return parsed.format("YYYY-MM-DD");
		}

		// Fallback: let moment try its default parsing
		const fallback = moment(trimmed);
		if (fallback.isValid()) {
			return fallback.format("YYYY-MM-DD");
		}

		return null;
	},

	/**
	 * Cleans the given item by trimming whitespace and removing double quotes.
	 *
	 * @param {string} item - The item to be cleaned.
	 * @returns {string} The cleaned item.
	 */
	cleanItem(item) {
		return item.trim().replace(/"/g, "");
	},

	/**
	 * Splits a cleaned item string into its date description and amount parts.
	 *
	 * @param {string} cleanedItem - The cleaned item string to be split.
	 * @returns {Object} An object containing the date description and amount.
	 * @returns {string} return.dateDescription - The date and description part of the item.
	 * @returns {string} return.amount - The amount part of the item.
	 */
	splitParts(cleanedItem) {
		const parts = cleanedItem.split(/,(?=\$|-)/);
		const [dateDescription, amount] = parts;
		return { dateDescription, amount };
	},

	/**
	 * Extracts the date and description from a comma-separated string.
	 *
	 * @param {string} dateDescription - A string containing the date and description separated by a comma.
	 * @returns {{ date: string, description: string }} An object containing the extracted date and description.
	 */
	extractDateDescription(dateDescription) {
		const [date, description] = dateDescription.split(",");
		return { date, description };
	},

	/**
	 * Parses a transaction item and extracts its date, description, and amount.
	 *
	 * @param {Object} item - The transaction item to be parsed.
	 * @returns {Object} An object containing the parsed date, description, and amount.
	 * @returns {string} return.date - The extracted date from the transaction item.
	 * @returns {string} return.description - The extracted description from the transaction item.
	 * @returns {number} return.amount - The extracted and cleaned amount from the transaction item.
	 */
	parseTransaction(item) {
		const cleanedItem = this.cleanItem(item);
		const { dateDescription, amount } = this.splitParts(cleanedItem);
		const { date, description } = this.extractDateDescription(dateDescription);
		const merchant = "";

		return {
			date: this.normalizeDate(date) || date,
			description,
			merchant,
			amount: parseFloat(amount.replace(/[$,]/g, ""))
		};
	},

	/**
	 * Cleans an array of lines by removing empty lines and lines that match a specific pattern.
	 *
	 * @param {Object} param - The parameter object.
	 * @param {string[]} param.lines - The array of lines to be cleaned.
	 * @returns {string[]} The cleaned array of lines.
	 *
	 */
	cleanFileArray({ lines }) {
		let cleanedLines = lines.filter(line => {
			return line !== `,,\r` && line !== "";
		});

		return cleanedLines;
	},

	/**
	 * Maps account descriptions to their corresponding accounts based on the provided data.
	 *
	 * @param {Object} params - The parameters for the function.
	 * @param {Array<Object>} params.casewareCategorizedAccounts - An array of categorized accounts from Caseware.
	 * @param {Array<Object>} params.priorYearTrialBalanceExtract - An array of prior year trial balance items.
	 * @returns {Array<string>} An array of full account descriptions in the format "accountNumber - accountDescription".
	 *                          If an account is not found, "Unclassified" is added to the array.
	 */
	mapAccountDescriptionToAccount({ casewareCategorizedAccounts, priorYearTrialBalanceExtract }) {
		let accountsWithDescriptions = [];
  
		casewareCategorizedAccounts.forEach((casewareCategorizedAccount) => {
		  let casewareMappedAccount = casewareCategorizedAccount.mappedAccount;
		  let account = priorYearTrialBalanceExtract.find((priorYearTrialBalanceItem) => priorYearTrialBalanceItem.accountNumber === casewareMappedAccount);
	  
		  if (!account){
			let fullAccountDescription = 'Unclassified';
			accountsWithDescriptions.push(fullAccountDescription);
			return;
		  };
	  
		  let { accountNumber, accountDescription } = account;
		  let fullAccountDescription = `${accountNumber} - ${accountDescription}`;
		  accountsWithDescriptions.push(fullAccountDescription);
		});
	  
		return accountsWithDescriptions;
	},

	/**
	 * Separates transactions into classified and unclassified groups based on their classification property.
	 *
	 * @param {Object} params - The input parameters.
	 * @param {Array<Object>} params.transactions - An array of transaction objects to be classified.
	 * @returns {Object} An object containing two arrays:
	 *   - `classifiedTransactions`: Transactions with a non-null, non-empty, and non-"Unclassified" classification.
	 *   - `unclassifiedTransactions`: Transactions with a null, empty, or "Unclassified" classification.
	 */
	getClassifiedAndUnclassifiedTransactions({ transactions }){
		const isUnclassified = (classification) => {
			if (classification === null || classification === undefined) return true;
			const value = String(classification).trim().toLowerCase();
			return value === "" || value === "unclassified" || value === "uncategorized";
		};

		let classifiedTransactions = transactions.filter(transaction => !isUnclassified(transaction.classification));

		let unclassifiedTransactions = transactions.filter(transaction => isUnclassified(transaction.classification));

		return {
			classifiedTransactions,
			unclassifiedTransactions
		};
	},

	/**
	 * Retrieves OpenAI account tax classifications and updates transaction data.
	 *
	 * @async
	 * @function
	 * @param {Object} params - The parameters for the function.
	 * @param {Array<Object>} params.unclassifiedTransactions - The list of unclassified transactions.
	 * @param {Array<Object>} params.openAiClassifications - The classifications provided by OpenAI for the transactions.
	 * @param {Array<Object>} params.classifiedTransactions - The list of already classified transactions.
	 * @param {string} params.province - The province for tax rate determination.
	 * @param {string} params.industry - The industry for tax rate determination.
	 * @returns {Promise<Object>} A promise that resolves to an object containing:
	 * - `openAiAccountsTaxClassifications` {Array<Object>} - The list of accounts with their corresponding tax rates.
	 * - `updatedTransactions` {Array<Object>} - The updated list of transactions with classifications and tax information.
	 */
	async getOpenAiAccountsTaxClassifications({unclassifiedTransactions, openAiClassifications, classifiedTransactions, province, industry }){
		console.groupCollapsed("build updatedTransactions from AI results");
		let updatedTransactions = unclassifiedTransactions.map((transaction, index) => {
			const result = (openAiClassifications && openAiClassifications[index]) || {};
			const updated = {
				...transaction,
				classification: result.classification || "Unclassified",
				tax: result.tax || ""
			};
			if (!result || !result.classification) {
				console.debug("AI result missing or unclassified", { index, description: transaction.description, merchant: transaction.merchant, amount: transaction.amount });
			} else {
				console.debug("AI classified", { index, classification: updated.classification, tax: updated.tax });
			}
			return updated;
		});
		console.groupEnd();

		updatedTransactions = [
			...classifiedTransactions,
			...updatedTransactions
		];

		const isUnclassified = (classification) => {
			if (classification === null || classification === undefined) return true;
			const value = String(classification).trim().toLowerCase();
			return value === "" || value === "unclassified" || value === "uncategorized";
		};

		let uniqueAccounts = new Set(
			updatedTransactions
				.map(transaction => transaction.classification)
				.filter(account => !isUnclassified(account))
		);

		let uniqueAccountsArray = Array.from(uniqueAccounts);

		let accountsWithTaxRates = uniqueAccountsArray.map(async account => {
			let taxRate = await this.getTaxRates({ account, province, industry });
			return {
				account,
				taxRate
			};
		});

		let openAiAccountsTaxClassifications = await Promise.all(accountsWithTaxRates);

		console.groupCollapsed("tax rate lookup summary");
		console.log("unique taxable accounts:", uniqueAccountsArray.length);
		console.table(uniqueAccountsArray.slice(0, 25).map((a, i) => ({ idx: i, account: a })));
		console.groupEnd();

		return { openAiAccountsTaxClassifications, updatedTransactions };
	},
};

export default BookkeepingService;
